import { useEffect, useState, useCallback, useRef } from "react";
import api, { fileUrl } from "../lib/api";
import { money, fmtDate, TREATMENTS } from "../lib/format";
import { toast } from "sonner";
import TransactionDialog from "./TransactionDialog";
import BulkScanDialog from "./BulkScanDialog";
import DailyTakingsDialog from "./DailyTakingsDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Plus, Search, Pencil, Trash2, AlertTriangle, Download, Paperclip, Camera, Coffee, Upload } from "lucide-react";

export default function TransactionLedger({ type }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [importProject, setImportProject] = useState("");
  const [search, setSearch] = useState("");
  const [treatment, setTreatment] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [takingsOpen, setTakingsOpen] = useState(false);
  const squareRef = useRef();
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { type };
    if (search) params.search = search;
    if (treatment !== "all") params.treatment = treatment;
    const [txns, cats] = await Promise.all([
      api.get("/transactions", { params }),
      api.get("/categories"),
    ]);
    setItems(txns.data);
    setCategories(cats.data);
    if (type === "income") {
      try { const p = await api.get("/projects"); setProjects(p.data); } catch { /* ignore */ }
    }
    setLoading(false);
  }, [type, search, treatment]);

  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    try {
      if (editing) await api.put(`/transactions/${editing.id}`, data);
      else await api.post("/transactions", data);
      toast.success(editing ? "Transaction updated" : "Transaction added");
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Could not save transaction");
    }
  };

  const remove = async (id) => {
    await api.delete(`/transactions/${id}`);
    toast.success("Deleted");
    load();
  };

  const importSquare = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (importProject) fd.append("project_id", importProject);
    try {
      const { data } = await api.post("/income/import-square", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extra = data.auto_repayments?.length ? ` · auto loan repayment ${data.auto_repayments.length}` : "";
      toast.success(`Imported ${data.created} sales from Square${extra}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not import the CSV");
    } finally {
      e.target.value = "";
    }
  };

  const exportCsv = () => {
    const rows = [["Date", "Description", type === "income" ? "Customer" : "Supplier", "Category", "Treatment", "Net", "GST", "Total"]];
    items.forEach((t) => rows.push([t.date, t.description, t.party, t.category, t.treatment, t.net_amount, t.gst_amount, t.amount]));
    const csv = rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${type}-ledger.csv`;
    a.click();
  };

  const totalGst = items.reduce((s, t) => s + t.gst_amount, 0);
  const totalNet = items.reduce((s, t) => s + t.net_amount, 0);
  const totalGross = items.reduce((s, t) => s + t.amount, 0);
  const isIncome = type === "income";

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {isIncome ? "Income & Sales" : "Expenses & Purchases"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isIncome ? "GST collected on your sales (1A)." : "GST credits you can claim on purchases (1B)."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn" className="gap-2">
            <Download className="w-4 h-4" /> Export
          </Button>
          {!isIncome && (
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-scan-btn" className="gap-2">
              <Camera className="w-4 h-4" /> Bulk scan
            </Button>
          )}
          {isIncome && (
            <>
              {projects.length > 0 && (
                <Select value={importProject || "none"} onValueChange={(v) => setImportProject(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-[160px]" data-testid="income-project-select"><SelectValue placeholder="No project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              <input ref={squareRef} type="file" accept=".csv" onChange={importSquare} className="hidden" data-testid="square-import-input" />
              <Button variant="outline" onClick={() => squareRef.current?.click()} data-testid="square-import-btn" className="gap-2">
                <Upload className="w-4 h-4" /> Import Square
              </Button>
              <Button variant="outline" onClick={() => setTakingsOpen(true)} data-testid="daily-takings-btn" className="gap-2">
                <Coffee className="w-4 h-4" /> Daily takings
              </Button>
            </>
          )}
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-transaction-btn" className="gap-2">
            <Plus className="w-4 h-4" /> Add {isIncome ? "income" : "expense"}
          </Button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        {[
          { label: "Net (ex GST)", value: totalNet },
          { label: isIncome ? "GST collected" : "GST claimable", value: totalGst, accent: "text-sky-700" },
          { label: "Total (incl GST)", value: totalGross },
        ].map((s) => (
          <div key={s.label} className="rounded-xl bg-card border border-border px-4 py-3">
            <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{s.label}</div>
            <div className={`num text-lg font-semibold mt-0.5 ${s.accent || ""}`}>{money(s.value)}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search description or party…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="ledger-search" />
        </div>
        <Select value={treatment} onValueChange={setTreatment}>
          <SelectTrigger className="w-[170px]" data-testid="ledger-treatment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All GST types</SelectItem>
            {Object.entries(TREATMENTS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50 hover:bg-secondary/50">
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="hidden md:table-cell">{isIncome ? "Customer" : "Supplier"}</TableHead>
                <TableHead className="hidden lg:table-cell">Category</TableHead>
                <TableHead>GST</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead className="text-right">GST amt</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  No {type} recorded yet. Click "Add {isIncome ? "income" : "expense"}".
                </TableCell></TableRow>
              ) : items.map((t) => {
                const tr = TREATMENTS[t.treatment];
                const missingInvoice = type === "expense" && t.amount >= 82.5 && !t.has_tax_invoice;
                return (
                  <TableRow key={t.id} className="row-hover" data-testid={`txn-row-${t.id}`}>
                    <TableCell className="num text-xs whitespace-nowrap">{fmtDate(t.date)}</TableCell>
                    <TableCell className="font-medium max-w-[220px]">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{t.description}</span>
                        {missingInvoice && (
                          <span title="No tax invoice held" data-testid={`no-invoice-${t.id}`}>
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          </span>
                        )}
                        {t.attachment_path && (
                          <a href={fileUrl(t.attachment_path)} target="_blank" rel="noreferrer"
                            title="View receipt" data-testid={`receipt-${t.id}`} className="text-primary shrink-0 hover:opacity-70 transition-opacity">
                            <Paperclip className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground text-sm">{t.party || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{t.category || "—"}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-md border font-medium ${tr.color}`}>{tr.short}</span>
                    </TableCell>
                    <TableCell className="num text-right text-sm">{money(t.net_amount)}</TableCell>
                    <TableCell className="num text-right text-sm text-sky-700">{money(t.gst_amount)}</TableCell>
                    <TableCell className="num text-right font-semibold">{money(t.amount)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <button onClick={() => { setEditing(t); setDialogOpen(true); }}
                          data-testid={`edit-txn-${t.id}`} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(t.id)}
                          data-testid={`delete-txn-${t.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <TransactionDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        type={type}
        categories={categories}
        onSave={save}
        editing={editing}
      />

      <BulkScanDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
      <DailyTakingsDialog open={takingsOpen} onOpenChange={setTakingsOpen} onDone={load} projectId={importProject} />
    </div>
  );
}
